import React from 'react';
import styled from 'styled-components';
import HBCheckboxField from '../HBCheckboxField/HBCheckboxField';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import type { ICheckboxBase } from '../HBCheckbox/HBCheckbox';
import type { HBCheckboxCustomTheme } from '../HBCheckbox/HBCheckbox.types';
import type { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useGroupFocusRedirect } from '../common/internal/useGroupFocusRedirect';

export type TCheckboxGroupOption<Value extends string | number = string> = {
    label: string;
    value: Value;
    disabled?: boolean;
};

interface _HBCheckboxGroupProps<Value extends string | number> {
    /** Checkbox items to render; each option's `value` is toggled in/out of the selected array on click. */
    options: ReadonlyArray<TCheckboxGroupOption<Value>>;
    /** Currently selected values. Pass an empty array for no selection. */
    value: Value[];
    /** Called with the updated selection array after each checkbox toggle. */
    onChange: (nextValues: Value[]) => void;
    /** Shared `name` attribute for all checkbox inputs; also used as the base for generating checkbox IDs. */
    name: string;
    /** Pixel gap between checkboxes; defaults to a size-dependent spacing (8px for sm/md, 12px for lg). */
    gap?: number;
    /** Layout direction of the checkbox items. */
    alignment?: 'horizontal' | 'vertical';
    /** Size of all checkboxes in the group. Defaults to `'md'`. */
    size?: ICheckboxBase['size'];
    /** Overrides the checked-state background color for all checkboxes in the group. */
    customTheme?: HBCheckboxCustomTheme;
    /** When true, applies error styling to all checkboxes in the group. */
    error?: boolean;
    /**
     * ID of the element that labels this group, used as the `aria-labelledby` attribute on the group container.
     * @deprecated Use `aria-labelledby`. When both are set, `aria-labelledby` wins.
     */
    ariaLabelledby?: string;
    /**
     * HTML id set on the `role="group"` container, so an `HBFieldControl` label
     * (`control.id`) can target it. When `aria-labelledby` is also set, focusing
     * the container moves focus to the first enabled checkbox. Without
     * `aria-labelledby` the id is set and nothing else changes. Omitted: no id.
     */
    id?: string;
    /** Accessible name for the group when the field has no visible label (e.g. an `HBFieldControl` with `ariaLabel`). Ignored when `aria-labelledby` or `ariaLabelledby` is set. Omitted: no attribute. */
    'aria-label'?: string;
    /** ID of the element that names the group (e.g. an `HBFieldControl` label). Omitted: falls back to `ariaLabelledby`. */
    'aria-labelledby'?: string;
    /** Space-separated IDs of hint/error elements describing the group. Omitted: no attribute. */
    'aria-describedby'?: string;
    /**
     * Marks every checkbox in the group invalid for assistive tech, so
     * `HBFieldControl`'s bundle can be passed as-is. Not set on the
     * `role="group"` container: ARIA does not support it there. Omitted: follows
     * `error` only.
     */
    'aria-invalid'?: boolean;
}

export type HBCheckboxGroupProps<Value extends string | number> =
    _HBCheckboxGroupProps<Value> & MustIncludeOneOfBasicTestIdProps;

const SPACING = {
    sm: 8,
    md: 8,
    lg: 12
} as const;

const HBCheckboxGroup = <Value extends string | number>({
    options,
    value,
    onChange,
    name,
    gap,
    alignment = 'vertical',
    size = 'md',
    customTheme,
    error,
    ariaLabelledby,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledByProp,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    testId,
    testIdName
}: HBCheckboxGroupProps<Value>) => {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || name,
        suffix: 'checkbox-group',
        componentName: 'HBCheckboxGroup',
        enforce: false
    });

    const isSelected = (val: Value) => value.includes(val);

    const handleToggle = (optionValue: Value) => {
        const nextValues = isSelected(optionValue)
            ? value.filter(v => v !== optionValue)
            : [...value, optionValue];
        onChange(nextValues);
    };

    const redirectsFocus = !!(id && ariaLabelledByProp);

    const focusRedirectProps = useGroupFocusRedirect({
        enabled: redirectsFocus,
        getTarget: group =>
            Array.from(
                group.querySelectorAll<HTMLInputElement>(
                    'input[type="checkbox"]'
                )
            ).find(checkbox => !checkbox.disabled)
    });

    return (
        <Container
            role="group"
            id={id}
            {...focusRedirectProps}
            aria-label={
                ariaLabelledByProp || ariaLabelledby ? undefined : ariaLabel
            }
            aria-labelledby={ariaLabelledByProp || ariaLabelledby}
            aria-describedby={ariaDescribedBy}
            gap={gap ?? SPACING[size]}
            alignment={alignment}
            data-testid={finalTestId}
        >
            {options.map(({ label, value: optionValue, disabled }) => {
                const optionId = `${name}-${optionValue}`;
                const checked = isSelected(optionValue);
                return (
                    <HBCheckboxField
                        isDSNext
                        key={optionValue}
                        id={optionId}
                        label={label}
                        placement="right"
                        size={size}
                        disabled={disabled}
                        error={error}
                        aria-invalid={ariaInvalid}
                        customTheme={customTheme}
                        checked={checked}
                        name={name}
                        value={optionValue}
                        onChange={() => handleToggle(optionValue)}
                    />
                );
            })}
        </Container>
    );
};

export default HBCheckboxGroup;

/* ------------------------------ Styles ------------------------------ */
const Container = styled.div<{
    gap: number;
    alignment: 'horizontal' | 'vertical';
}>`
    display: flex;
    flex-direction: ${({ alignment }) =>
        alignment === 'horizontal' ? 'row' : 'column'};
    gap: ${({ gap }) => gap}px;
`;
